import { ref, watch } from 'vue'
import ApiService from '@/services/api.js'

export function useNews(locale) {
  // Reactive state
  const newsData = ref([])
  const isLoadingData = ref(false)
  const imageCache = new Map()
  const loadingImages = new Set()
  const failedImages = new Set()
  
  // Default images
  const defaultImages = {
    news: 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    placeholder: 'https://images.unsplash.com/photo-1497440001374-f26997328c1b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
  }

  // News yükleme
  const loadNews = async () => {
    try {
      console.log('🔄 Loading news...')
      isLoadingData.value = true
      
      const newsApiData = await ApiService.getNews()
      console.log('📰 News API response:', newsApiData)
      
      if (newsApiData && newsApiData.length > 0) {
        newsData.value = newsApiData.slice(0, 6).map(article => ({
          id: article.id,
          title: locale?.value === 'tr' ? (article.title_tr || article.title) : (article.title_en || article.title) || (locale?.value === 'tr' ? 'Haber Başlığı' : 'News Title'),
          excerpt: locale?.value === 'tr' ? (article.excerpt_tr || article.excerpt) : (article.excerpt_en || article.excerpt) || article.content?.substring(0, 150) + '...' || (locale?.value === 'tr' ? 'Haber özeti...' : 'News excerpt...'),
          date: formatDate(article.created_at || article.date, locale?.value),
          image: article.image || article.image_url || defaultImages.news
        }))
        console.log('✅ News loaded:', newsData.value.length, 'articles')
      } else {
        console.warn('⚠️ No news data received, using fallback')
        setFallbackNews()
      }
    } catch (error) {
      console.error('❌ Haberler yüklenemedi:', error)
      setFallbackNews()
    } finally {
      isLoadingData.value = false
    }
  }

  // Fallback news
  const setFallbackNews = () => {
    newsData.value = [
      {
        id: 1,
        title: locale?.value === 'tr' ? 'Yeni Güneş Enerjisi Projesi' : 'New Solar Energy Project',
        excerpt: locale?.value === 'tr' ? 'Aslan Energy olarak yeni güneş enerjisi projesimizi başlattık...' : 'As Aslan Energy, we launched our new solar energy project...',
        date: formatDate(new Date().toISOString(), locale?.value),
        image: defaultImages.news
      },
      {
        id: 2,
        title: locale?.value === 'tr' ? 'Sürdürülebilir Enerji Hedefleri' : 'Sustainable Energy Goals',
        excerpt: locale?.value === 'tr' ? '2030 hedeflerimiz doğrultusunda yeni yatırımlar...' : 'New investments in line with our 2030 targets...',
        date: formatDate(new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(), locale?.value),
        image: 'https://images.unsplash.com/photo-1466611653911-95081537e5b7?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
      },
      {
        id: 3,
        title: locale?.value === 'tr' ? 'Teknoloji Yenilikleri' : 'Technology Innovations',
        excerpt: locale?.value === 'tr' ? 'Son teknoloji ürünlerimizle verimliliği artırıyoruz...' : 'We increase efficiency with our latest technology products...',
        date: formatDate(new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(), locale?.value),
        image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
      }
    ]
  }

  // Date formatting
  const formatDate = (dateString, currentLocale = 'tr') => {
    if (!dateString) return new Date().toLocaleDateString(currentLocale === 'tr' ? 'tr-TR' : 'en-US')
    
    try {
      return new Date(dateString).toLocaleDateString(currentLocale === 'tr' ? 'tr-TR' : 'en-US')
    } catch (error) {
      return dateString
    }
  }

  // Image error handling
  const handleImageError = (event, imageType = 'news') => {
    const originalSrc = event.target.src
    failedImages.add(originalSrc)
    loadingImages.delete(originalSrc)
    
    const fallbackUrl = defaultImages[imageType] || defaultImages.placeholder
    if (event.target.src !== fallbackUrl) {
      event.target.src = fallbackUrl
      console.log(`🔄 Fallback image set for ${imageType}:`, fallbackUrl)
    }
  }

  // Watch locale changes
  if (locale) {
    watch(locale, () => {
      loadNews()
    })
  }

  // Initialize
  loadNews()

  return {
    newsData,
    isLoadingData,
    imageCache,
    loadingImages,
    handleImageError,
    loadNews
  }
}